import React from "react";
import { Box, Flex, Text, Badge, VStack, SimpleGrid } from "@chakra-ui/react";
import useThemeColors from "../../../../hooks/useThemeColors";

interface AuditTrailEntry {
  id: string;
  entityName: string;
  entityId: string;
  action: "CREATE" | "UPDATE" | "DELETE";
  applicationUserId: string;
  oldValues: string;
  newValues: string;
  changes: string;
  ipAddress: string;
  userAgent: string;
  additionalData: string;
  createdAt: string;
}

interface AuditTrailCardProps {
  entry: AuditTrailEntry;
  onRowClick: (entry: AuditTrailEntry) => void;
  getUserInfo: (userId: string) => { name: string; email: string };
  getUserAgentInfo: (userAgent: string) => { browser: string; device: string };
  getActionColor: (action: string) => string;
  formatDate: (dateString: string) => string;
}

const AuditTrailCard: React.FC<AuditTrailCardProps> = ({
  entry,
  onRowClick,
  getUserInfo,
  getUserAgentInfo,
  getActionColor,
  formatDate,
}) => {
  const colors = useThemeColors();
  const { browser, device } = getUserAgentInfo(entry.userAgent);

  return (
    <Box
      p={4}
      border="1px solid"
      borderColor={colors.border.secondary}
      borderRadius="lg"
      cursor="pointer"
      _hover={{
        bg: colors.background.tertiary,
      }}
      onClick={() => onRowClick(entry)}
    >
      <VStack gap={3} align="stretch">
        <Flex justify="space-between" align="center">
          <Badge colorPalette={getActionColor(entry.action)}>
            {entry.action}
          </Badge>
          <Text fontSize="xs" color="gray.500">
            {formatDate(entry.createdAt)}
          </Text>
        </Flex>

        <Box>
          <Text fontSize="sm" fontWeight="medium">
            {entry.entityName}
          </Text>
          <Text fontSize="xs" color="gray.500" fontFamily="mono">
            ID: {entry.entityId}
          </Text>
        </Box>

        <Text lineClamp={2} fontSize="sm">
          {entry.changes}
        </Text>

        <SimpleGrid columns={2} gap={3}>
          <Box>
            <Text fontSize="xs" fontWeight="medium" color="gray.500">
              User
            </Text>
            <Text fontSize="sm">
              {getUserInfo(entry.applicationUserId).name}
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="medium" color="gray.500">
              Device
            </Text>
            <Text fontSize="sm">
              {browser} ({device})
            </Text>
          </Box>
        </SimpleGrid>

        <Text fontSize="xs" color="gray.500" fontFamily="mono">
          IP: {entry.ipAddress}
        </Text>
      </VStack>
    </Box>
  );
};

export default AuditTrailCard;
